import { ref, watch } from 'vue'
import { listConversations, type ConversationListItem } from '@/services/wootdashConversations'
import { onListChanged, onConversationRead, onRealtimeRecovered } from '@/composables/useConversationRealtime'
import { currentOpenConvId } from '@/composables/useChatTrigger'
import { useCurrentAgentRole } from '@/composables/useCurrentAgentRole'
import { hiddenFluxoIdsCp, isFluxoHiddenCp } from '@/composables/useHiddenFluxosCp'

// Faixa de avatares do rail global (ConvDrawer.vue) — diferente de useMyActiveConversations.ts
// (usado por /foco, que é sempre "minhas conversas", em qualquer perfil, de propósito: é uma
// fila de trabalho pessoal). Aqui o critério muda por perfil: Administrador/Sistema (sem
// restrição de time, ver useCurrentAgentRole.ts) veem toda conversa ativa do sistema — faz
// sentido pra quem está navegando o Dashboard de supervisor e abrindo conversas de qualquer
// agente; os demais perfis continuam vendo só as próprias (mesmo escopo de sempre).
const ACTIVE_PAGE_SIZE = 200
// Espelha o filtro `statuses: ['Open', 'Pending']` do refetch abaixo (enum numérico do
// backend: Open=0, Pending=1).
const ACTIVE_STATUSES = new Set([0, 1])

const railConversations = ref<ConversationListItem[]>([])
let started = false
let unsubscribeListChanged: (() => void) | null = null
let unsubscribeRead: (() => void) | null = null
let stopRecovered: (() => void) | null = null
let currentToken = ''
let currentAgentId = ''

const { isUnrestricted } = useCurrentAgentRole()

async function refetch() {
  if (!currentToken) return
  const { conversations } = await listConversations(currentToken, {
    onlyMine: !isUnrestricted.value,
    statuses: ['Open', 'Pending'],
    pageSize: ACTIVE_PAGE_SIZE,
    // "Esconder Fluxo" (DashHeader.vue) — mesmo filtro do motor do Dashboard
    // (useWootDashLiveData.ts); aqui cobre a faixa de avatares do ConvDrawer.vue.
    excludeFlowIds: hiddenFluxoIdsCp.value,
  })
  railConversations.value = conversations.filter(c => !isFluxoHiddenCp(c.flowId))
}

// Reconcilia uma única conversa a partir do payload de "ListaDeConversasAlterada" — evita
// o refetch completo a cada evento (plano-performance-escalabilidade.md item 3). Mesmo
// critério de visibilidade do refetch acima: sem restrição vê qualquer conversa ativa,
// os demais só a própria; "Esconder Fluxo" também se aplica aqui.
function applyListItem(item: ConversationListItem): void {
  const visible = isUnrestricted.value || item.agentId === currentAgentId
  const belongs = visible && ACTIVE_STATUSES.has(item.status) && !isFluxoHiddenCp(item.flowId)
  const idx = railConversations.value.findIndex(c => c.id === item.id)
  if (!belongs) {
    if (idx !== -1) railConversations.value.splice(idx, 1)
    return
  }
  const prev = idx !== -1 ? railConversations.value[idx] : undefined
  const withUnread = { ...item, unreadCount: nextUnreadCount(prev, item, isBeingViewed(item.id)) }
  if (idx !== -1) railConversations.value.splice(idx, 1, withUnread)
  else railConversations.value = [withUnread, ...railConversations.value]
}

/** Conversa aberta no painel com a aba visível — o agente está vendo, então não acumula. */
function isBeingViewed(conversationId: string): boolean {
  const tabVisible = typeof document === 'undefined' || document.visibilityState === 'visible'
  return tabVisible && currentOpenConvId.value === conversationId
}

/**
 * Badge de não lidas a partir de um item empurrado por SignalR. O item é o mesmo pra todos os agentes
 * (vem com unreadCount 0), então a contagem é local: mensagem nova do contato (lastMessageAt mudou)
 * soma 1; o resto mantém. Conversa que acabou de entrar no rail sem histórico local começa em 1 se a
 * última for do contato — o refetch (até 60 s, ou o próximo GET) traz o número exato do servidor.
 */
export function nextUnreadCount(prev: ConversationListItem | undefined, item: ConversationListItem, beingViewed: boolean): number {
  if (beingViewed) return 0
  const newContactMessage = item.lastMessageSenderType === 'Contact' && !!item.lastMessageAt && item.lastMessageAt !== prev?.lastMessageAt
  if (!prev) return newContactMessage ? 1 : 0
  const base = prev.unreadCount ?? 0
  return newContactMessage ? base + 1 : base
}

/** O agente viu a conversa (abriu no painel, ou outra aba dele avisou por "ConversaLida"). */
export function markRailRead(conversationId: string): void {
  const idx = railConversations.value.findIndex(c => c.id === conversationId)
  if (idx === -1 || !railConversations.value[idx].unreadCount) return
  railConversations.value.splice(idx, 1, { ...railConversations.value[idx], unreadCount: 0 })
}

// isUnrestricted carrega de forma assíncrona (useCurrentAgentRole, ver App.vue) — se o rail
// já tiver iniciado antes disso resolver, o primeiro refetch usaria "só minhas" por engano
// pra um perfil sem restrição. Reagir à mudança corrige assim que o papel real chegar.
watch(isUnrestricted, () => { if (started) refetch() })
// Trocar "Esconder Fluxo" refaz a busca na hora, sem esperar o próximo evento realtime/poll.
watch(hiddenFluxoIdsCp, () => { if (started) refetch() })

export function useRailConversations() {
  return { railConversations }
}

/** Idempotente — chamar de novo com o motor já rodando só atualiza token/agentId. */
export function startRailConversations(token: string, agentId = ''): void {
  currentToken = token
  currentAgentId = agentId
  if (started) { refetch(); return }
  started = true
  refetch()
  unsubscribeListChanged = onListChanged(applyListItem)
  unsubscribeRead = onConversationRead(evt => markRailRead(evt.conversationId))
  // Sem polling: o merge incremental cobre o dia a dia, e o que se perder numa queda da conexão é rebuscado
  // quando ela volta (onRealtimeRecovered) — substitui o refetch a cada 60 s que existia só pra cobrir isso.
  stopRecovered = onRealtimeRecovered(() => refetch())
}

export function stopRailConversations(): void {
  started = false
  currentToken = ''
  currentAgentId = ''
  railConversations.value = []
  unsubscribeListChanged?.()
  unsubscribeListChanged = null
  unsubscribeRead?.()
  unsubscribeRead = null
  stopRecovered?.()
  stopRecovered = null
}
